/**
 * The Reports landing gallery.
 *
 * Reported: "The reports screen seems basic. Make it pretty. Remember SIMPLE
 * and PRETTY."
 *
 * It was seventeen identical white rectangles, each carrying a three-line
 * paragraph, stacked in a flat grid. Everything had the same visual weight, so
 * nothing was findable — you read the whole page to locate one report, and the
 * prose made it a page of prose.
 *
 * What changed and why:
 *
 *  - SEARCH FIRST. Seventeen reports with 188 fields between them is a corpus,
 *    not a menu. The box filters on name, description AND field names, so
 *    typing "bitlocker" finds Disk encryption even though the word is not in
 *    its title.
 *  - A COLOURED ICON PER CATEGORY. Colour does the grouping work that four
 *    identical grey subheadings could not, and it survives peripheral vision —
 *    you learn "operations is amber" and stop reading headings.
 *  - ONE LINE OF DESCRIPTION. The full text is still there on hover; it should
 *    not cost three lines of every card before you have chosen anything.
 *  - ACTIONS ON THE CARD. Run, CSV and PDF without opening the report first.
 *    Downloading used to mean run → wait → download.
 *
 * Deliberately NOT a table. These are things to pick, and a table of
 * seventeen rows with a description column is the layout being replaced.
 */
import { useMemo } from "react";

export type GalleryType = {
  key: string;
  label: string;
  description: string;
  category: string;
  default_columns: string[];
  columns: { key: string; label: string }[];
};

/** A generated document rather than a row grid: a PDF, CSV or zip built by
 *  services/document_reports.py. Same gallery, different action. */
export type GalleryDocument = {
  key: string;
  label: string;
  description: string;
  category: string;
  needs_customer: boolean;
  formats: string[];
};

/** Generic over the caller's own type.
 *
 * GalleryType is the minimum this component reads. The page's TypeDef has more
 * on it (portal_safe, ops_by_type), and its handlers are typed against TypeDef
 * — so a non-generic `onRun: (t: GalleryType) => void` would refuse them: the
 * gallery cannot promise to hand back the extra fields. Generic keeps both the
 * narrow contract here and full type safety at the call site. */
type Props<T extends GalleryType, D extends GalleryDocument> = {
  types: T[];
  busy?: boolean;
  onRun: (t: T) => void;
  onDownload: (t: T, fmt: "csv" | "pdf") => void;
  onBuild: () => void;
  /** Owned by the page so the saved-reports list answers to the same box.
   *  One search that filters half the screen reads as "the rest matched". */
  query: string;
  onQueryChange: (v: string) => void;
  /** Generated documents, merged into the same categories as the tables. */
  documents?: D[];
  onRunDocument?: (d: D, fmt: string) => void;
  /** Shown in the empty state so "nothing matches" is not contradicted by a
   *  table of saved reports directly beneath it. */
  savedMatchCount?: number;
};

/** Words people type that appear nowhere in the data.
 *
 * Checked against the live registry rather than assumed: "bitlocker" and
 * "offline" matched NOTHING across all 17 types and 188 field names, and the
 * product spells it "licence", so a US technician typing "license" missed the
 * Microsoft 365 licence report entirely. A search that returns nothing for the
 * word someone actually knows is the opposite of simple.
 *
 * Extra terms are appended to a type's haystack, never shown — this only ever
 * widens what matches.
 */
const SYNONYMS: Record<string, string> = {
  disk_encryption: "bitlocker filevault luks encrypted volumes recovery compliance evidence",
  devices: "offline online down unreachable asset inventory endpoint workstation server reboot restart uptime",
  m365_licenses: "license licenses subscription subscriptions sku cost spend unused wasted seats o365 office365 microsoft365",
  m365_users: "license licenses o365 office365 microsoft365 entra azure mailbox mfa",
  hybrid_users: "license licenses o365 office365 azure entra sync mismatch orphan",
  domain_users: "ad active directory account accounts login logon disabled locked upn",
  domain_groups: "ad active directory security group membership permissions access",
  patches: "update updates patching windows update missing kb hotfix vulnerability",
  software_inventory: "apps applications programs installed version software audit",
  software_deployments: "deploy deployment install package push rollout",
  backups: "backup restore veeam recovery rpo dr",
  certificates: "ssl tls cert certs expiry expiring https chain",
  url_monitors: "website uptime downtime http https monitor availability response",
  warranty: "support contract entitlement expiry refresh lifecycle eol",
  tickets: "helpdesk service desk sla incident request",
  time_entries: "billing hours billable timesheet labour labor",
  alerts: "alarm alerting notification threshold warning critical noise",
  // Searched for by vendor and by symptom far more often than by our label -
  // somebody preparing for a call about a bad line types "meraki" or
  // "flapping", never "network devices".
  network_devices:
    "network switch router firewall access point ap wifi wireless meraki unifi " +
    "ubiquiti cisco snmp icmp port link uplink wan lan flapping drops outage " +
    "gateway printer infrastructure poe",
};

/** Icon + colour per category. Unknown categories fall back to slate rather
 *  than vanishing, so a new report type added server-side still renders. */
const CATEGORY_STYLE: Record<
  string,
  { icon: string; ring: string; bg: string; text: string; dot: string }
> = {
  Devices: {
    icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
    ring: "group-hover:border-blue-400 dark:group-hover:border-blue-500",
    bg: "bg-blue-50 dark:bg-blue-500/10",
    text: "text-blue-600 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  "Users & licences": {
    icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
    ring: "group-hover:border-violet-400 dark:group-hover:border-violet-500",
    bg: "bg-violet-50 dark:bg-violet-500/10",
    text: "text-violet-600 dark:text-violet-400",
    dot: "bg-violet-500",
  },
  Operations: {
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
    ring: "group-hover:border-amber-400 dark:group-hover:border-amber-500",
    bg: "bg-amber-50 dark:bg-amber-500/10",
    text: "text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  Network: {
    // Wi-Fi arcs over a node: reads as "network gear" at 20px, where a
    // switch chassis outline turns into a grey smudge.
    icon: "M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.212 0",
    ring: "group-hover:border-cyan-400 dark:group-hover:border-cyan-500",
    bg: "bg-cyan-50 dark:bg-cyan-500/10",
    text: "text-cyan-600 dark:text-cyan-400",
    dot: "bg-cyan-500",
  },
  "Service delivery": {
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
    ring: "group-hover:border-emerald-400 dark:group-hover:border-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    text: "text-emerald-600 dark:text-emerald-400",
    dot: "bg-emerald-500",
  },
};
const FALLBACK = {
  icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  ring: "group-hover:border-slate-400",
  bg: "bg-slate-100 dark:bg-slate-700",
  text: "text-slate-600 dark:text-slate-300",
  dot: "bg-slate-400",
};

/** Does `hay` match every word of `needle`?
 *
 * Short words match on a WORD BOUNDARY, longer ones anywhere. Substring
 * matching on a two-letter query is useless: "ai" hit dom-AI-n, av-AI-lable and
 * em-AI-l, so typing it returned 22 of the 25 cards instead of the AI ones.
 * Three characters is the cut — "ssl" and "qbr" are real words a user types,
 * and they are specific enough not to collide.
 */
function matches(hay: string, needle: string): boolean {
  return needle.split(/\s+/).every((w) => {
    if (!w) return true;
    if (w.length > 3) return hay.includes(w);
    // Word-start scan rather than a RegExp: the needle is user input, so a
    // regex would need escaping, and escaping a character class inside a
    // generated file is how this line got mangled the first time.
    let i = hay.indexOf(w);
    while (i !== -1) {
      const before = i === 0 ? " " : hay[i - 1];
      if (!/[a-z0-9]/.test(before)) return true;
      i = hay.indexOf(w, i + 1);
    }
    return false;
  });
}

function Icon({ d, className }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
    </svg>
  );
}

export default function ReportGallery<T extends GalleryType, D extends GalleryDocument>({
  types,
  busy,
  onRun,
  onDownload,
  onBuild,
  query,
  onQueryChange,
  savedMatchCount = 0,
  documents = [],
  onRunDocument,
}: Props<T, D>) {
  const q = query;
  const setQ = onQueryChange;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return types;
    return types.filter((t) => {
      // Field names are searched too: "bitlocker" should find Disk encryption
      // even though the word is not in its title or description.
      const hay = [
        t.label,
        t.description,
        t.category,
        ...(t.columns || []).map((c) => `${c.label} ${c.key}`),
        SYNONYMS[t.key] || "",
      ]
        .join(" ")
        .toLowerCase();
      return matches(hay, needle);
    });
  }, [types, q]);

  const filteredDocs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return documents;
    return documents.filter((d) => {
      const hay = `${d.label} ${d.description} ${d.category}`.toLowerCase();
      return matches(hay, needle);
    });
  }, [documents, q]);

  // Categories come from both lists, so an AI-insights section appears even
  // though no TABULAR report lives there.
  const docsByCategory = useMemo(() => {
    const m = new Map<string, D[]>();
    filteredDocs.forEach((d) => {
      const k = d.category || "Documents";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(d);
    });
    return m;
  }, [filteredDocs]);

  const grouped = useMemo(() => {
    const m = new Map<string, T[]>();
    filtered.forEach((t) => {
      const k = t.category || "Other";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    });
    return Array.from(m.entries());
  }, [filtered]);

  /** Every category present in either list, tables first within a category. */
  const allCategories = useMemo(() => {
    const names = new Set<string>();
    grouped.forEach(([c]) => names.add(c));
    docsByCategory.forEach((_v, c) => names.add(c));
    return Array.from(names);
  }, [grouped, docsByCategory]);

  return (
    <div>
      {/* Search + build, on one line. The search is the primary control on this
          screen, so it gets the width. */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reports and fields — try “bitlocker”, “license”, “offline”"
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <button
          onClick={onBuild}
          className="shrink-0 inline-flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-blue-700 shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Build a report
        </button>
      </div>

      {filtered.length === 0 && filteredDocs.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {savedMatchCount > 0
              ? `No standard report matches “${q}” — but ${savedMatchCount} saved report${
                  savedMatchCount === 1 ? "" : "s"
                } below do.`
              : `Nothing matches “${q}”.`}
          </p>
          <button
            onClick={onBuild}
            className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Build a report instead →
          </button>
        </div>
      )}

      {allCategories.map((category) => {
        const items = grouped.find(([c]) => c === category)?.[1] || [];
        const docs = docsByCategory.get(category) || [];
        const st = CATEGORY_STYLE[category] || FALLBACK;
        return (
          <section key={category} className="mb-7">
            <div className="flex items-center gap-2 mb-3">
              <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {category}
              </h2>
              <span className="text-[11px] text-gray-400 dark:text-gray-600">
                {items.length + docs.length}
              </span>
            </div>

            {/* The cards carry min-w-0. Without it a grid auto-track will
                not shrink below its item's MIN-CONTENT width, and the
                description below uses `truncate` - which includes
                whitespace-nowrap - so min-content was the entire sentence.
                One column at phone width, and each card measured 1123px:
                the gallery scrolled sideways and every description was cut
                off mid-sentence. The truncate was doing its job; the track
                was refusing to give it a width to truncate against. */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((t) => (
                <div
                  key={t.key}
                  className={`group relative min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 transition-all hover:shadow-md ${st.ring}`}
                >
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onRun(t)}
                    className="text-left w-full disabled:opacity-60 disabled:cursor-wait"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${st.bg} ${st.text}`}
                      >
                        <Icon d={st.icon} className="w-[18px] h-[18px]" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                          {t.label}
                        </span>
                        {/* One line. The whole description is on hover — it
                            should not cost three lines of every card before
                            anyone has chosen anything. */}
                        <span
                          className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate"
                          title={t.description}
                        >
                          {t.description}
                        </span>
                      </span>
                    </div>
                  </button>

                  {/* Actions. Visible on hover/focus on a pointer device, and
                      always visible on touch, where there is no hover. */}
                  <div className="mt-3 flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onRun(t)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-60 ${st.text} border-transparent ${st.bg} hover:brightness-95`}
                    >
                      Run
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onDownload(t, "csv")}
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                    >
                      CSV
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onDownload(t, "pdf")}
                      className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
                    >
                      PDF
                    </button>
                    <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-600">
                      {t.columns?.length || 0} fields
                    </span>
                  </div>
                </div>
              ))}

              {/* Documents. No row grid to preview, so the actions are the
                  formats it can produce. */}
              {docs.map((d) => (
                <div
                  key={d.key}
                  className={`group relative min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 transition-all hover:shadow-md ${st.ring}`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${st.bg} ${st.text}`}
                    >
                      <Icon
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        className="w-[18px] h-[18px]"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                        {d.label}
                      </span>
                      <span
                        className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate"
                        title={d.description}
                      >
                        {d.description}
                      </span>
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    {d.formats.map((f) => (
                      <button
                        key={f}
                        type="button"
                        disabled={busy}
                        onClick={() => onRunDocument && onRunDocument(d, f)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-lg border border-transparent transition-colors disabled:opacity-60 ${st.text} ${st.bg} hover:brightness-95`}
                      >
                        {f.toUpperCase()}
                      </button>
                    ))}
                    <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-600">
                      {d.needs_customer ? "per customer" : "document"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
